import { LitElement, html, css } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '../lib/lit-styles.js';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '../lib/lit-3form-block/styles.js';
import Vimeo from '@vimeo/player';

export class VimeoVideo extends LitElement {
  static get properties() {
    return {
      videoId: { type: Number },
      videoWidth: { type: Number },
      minHeight: { type: Number },
      fullHeight: { type: Boolean },
      ctaAnalyticsData: { type: Object },
      videoThumb: { type: String },
      videoThumbClicked: { type: Boolean },
      player: { type: Vimeo },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      css`
        div#video-player-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background-color: #000;
        }

        iframe {
          max-height: 100%;
          max-width: 100%;
        }

        .hide {
          display: none !important;
        }

        #frame {
          position: relative;
          width: 100%;
        }

        #video-player {
          width: 100%;
        }

        #video-player.full-height {
          height: 100%;
        }

        #frame img {
          display: block;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          max-width: 100%;
          max-height: 100%;
        }

        .play-button {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 70px;
          background-color: rgba(0, 0, 0, 0.8);
          border-radius: 5px;
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          border: none;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .play-icon {
          width: 0;
          height: 0;
          border-left: 25px solid rgba(255, 255, 255, 0.8);
          border-top: 15px solid transparent;
          border-bottom: 15px solid transparent;
          transition: border-left-color 0.3s ease;
        }

        #frame:hover {
          cursor: pointer;
        }

        #frame:hover .play-button {
          background-color: rgba(255, 153, 61, 1);
        }

        #frame:hover .play-icon {
          border-left: 25px solid rgba(255, 255, 255, 1);
        }
      `,
    ];
  }

  constructor() {
    super();
    this.ctaAnalyticsData = {};
    this.videoThumb = '';
    this.videoThumbClicked = false;
    this.player = null;
    this.videoWidth = null;
    this.minHeight = null;
    this.fullHeight = true;
  }

  connectedCallback() {
    super.connectedCallback();

    const hostElement = this.shadowRoot.host;
    if (hostElement?.id === 'hero-media') {
      // The hero-media id comes from the Walter carousel and requires the
      // video player to be positioned differently.
      this.fullHeight = false;
    }

    const checkInterval = setInterval(() => {
      if (this.shadowRoot.querySelector('div#video-player-wrapper')) {
        this.setupThumbnailCover();
        this.setupVideoPlayer();
        this.setupMinHeightConstraint();
        clearInterval(checkInterval);
      }
    }, 50);
  }

  render() {
    return html`
      <div id="video-player-wrapper">
        <div
          id="video-player"
          class="${classMap({
            show: this.videoThumbClicked,
            hide: !this.videoThumbClicked,
            'full-Height': this.fullHeight,
          })}"
        ></div>
        <div
          id="frame"
          @click="${this.handleVideoThumbClick}"
          class="${classMap({
            show: !this.videoThumbClicked,
            hide: this.videoThumbClicked,
          })}"
        >
          <img
            id="videoThumbnail"
            referrerpolicy="origin"
            decoding="async"
            loading="lazy"
            src="${this.videoThumb}"
            aria-label="Play: Video"
            alt="Play: Video"
          />
          <button class="play-button" aria-label="Play">
            <span class="play-icon" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    `;
  }

  async setupVideoPlayer() {
    const videoPlayer = this.shadowRoot.querySelector('div#video-player');

    const options = {
      id: this.videoId,
      responsive: true,
    };

    this.player = new Vimeo(videoPlayer, options);
  }

  get showVideoThumb() {
    return !!this.videoThumb;
  }

  handleVideoThumbClick() {
    this.videoThumbClicked = !this.videoThumbClicked;
    this.player.play();
  }

  async setupThumbnailCover() {
    if (this.parsedVideoId) {
      await fetch(`https://vimeo.com/api/v2/video/${this.parsedVideoId}.json`)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(body => {
          if (body[0]) {
            const width = `_${this.parsedVideoWidth}`;
            this.videoThumb = body[0].thumbnail_large.replace('_640', width);
          }
        });
    }
  }

  get parsedVideoId() {
    if (this.videoId) return this.videoId;

    this.videoId = this.getAttribute('videoid')
      ? JSON.parse(this.getAttribute('videoid'))
      : null;

    return this.videoId;
  }

  get parsedVideoWidth() {
    if (this.videoWidth) return this.videoWidth;

    this.videoWidth = this.getAttribute('videoWidth')
      ? JSON.parse(this.getAttribute('videoWidth'))
      : 1000;

    return this.videoWidth;
  }

  get parsedMinHeight() {
    if (this.minHeight) return this.minHeight;

    this.minHeight = this.getAttribute('minHeight')
      ? JSON.parse(this.getAttribute('minHeight'))
      : 350;

    return this.minHeight;
  }

  setupMinHeightConstraint() {
    if (this.parsedMinHeight) {
      this.shadowRoot.querySelector(
        'div#video-player-wrapper'
      ).style.minHeight = `${this.minHeight}px`;
      this.shadowRoot.querySelector(
        'div#frame'
      ).style.height = `${this.minHeight}px`;
      this.requestUpdate();
    }
  }
}
customElements.define('vimeo-video', VimeoVideo);
